import clsx from "clsx";
import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { DropdownItem } from "@dashboard/devices/components/shared";
import DropdownContainer from "@dashboard/devices/components/shared/Dropdown/DropdownContainer";
// eslint-disable-next-line no-restricted-imports
import { Box, BoxProps, Button, ButtonProps } from "@mui/material";

import { Tooltip, TooltipProps } from "./Tooltip";

type PrivateButton = Omit<ButtonProps, "tooltipProps"> & {
    tooltipProps?: Omit<TooltipProps, "arrow" | "children">;
};

const useStyles = makeStyles<{
    fullWidth?: boolean;
}>()((_theme, { fullWidth }) => ({
    fullWidth: {
        width: fullWidth ? "100%" : "unset",
    },
    baseStyle: {
        height: "40px",
        minWidth: 40,
        fontSize: "14px",
        width: fullWidth ? "100%" : "unset",
        whiteSpace: "nowrap",
        borderRadius: 8,
    },
    splitButton: {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        minWidth: 40,
    },
    // Doubled specificity so these win over baseStyle, which is applied by BaseButton on the same element
    compactIcon: {
        "&&": {
            width: 32,
            minWidth: 32,
            height: 32,
            padding: 0,
            "& i": {
                fontSize: 14,
            },
        },
    },
}));

const hasTextContent = (children: React.ReactNode): boolean =>
    React.Children.toArray(children).some((child) => {
        if (typeof child === "number") {
            return true;
        }
        if (typeof child === "string") {
            return child.trim().length > 0;
        }
        return React.isValidElement<{ children?: React.ReactNode }>(child) && hasTextContent(child.props.children);
    });

const BaseButton = forwardRef<HTMLButtonElement, PrivateButton>((props, ref) => {
    const { tooltipProps, loading, fullWidth, ...rest } = props;
    const { classes } = useStyles({ fullWidth });

    // An icon-only button renders no text node, and the tooltip labels the wrapper element rather
    // than the button, so the button reaches screen readers (and role queries) without any name.
    // Its tooltip is the label the design system already asks callers to provide, so reuse it.
    const tooltipTitle = tooltipProps?.title;
    const iconOnlyLabel = typeof tooltipTitle === "string" && !hasTextContent(rest.children) ? tooltipTitle : undefined;

    return (
        <Tooltip placement="top-start" {...tooltipProps} className={clsx(classes.fullWidth, tooltipProps?.className)}>
            <Button
                ref={ref}
                disableRipple={false}
                loading={loading}
                {...rest}
                aria-label={rest["aria-label"] ?? iconOnlyLabel}
                className={clsx(classes.baseStyle, rest.className)}
            >
                {rest.children}
            </Button>
        </Tooltip>
    );
});

type PublicButton = Omit<PrivateButton, "color" | "variant">;

export const PrimaryButton = forwardRef<HTMLButtonElement, PublicButton>((props: PublicButton, ref) => {
    return <BaseButton ref={ref} {...props} variant="contained" color="primary" />;
});
export const PrimaryOutlinedButton = forwardRef<HTMLButtonElement, PublicButton>((props: PublicButton, ref) => {
    return <BaseButton ref={ref} {...props} variant="outlined" color="primary" />;
});

export const SecondaryButton = forwardRef<HTMLButtonElement, PublicButton>((props: PublicButton, ref) => {
    return <BaseButton ref={ref} {...props} variant="contained" color="secondary" />;
});

/**
 * Compact 32px square icon-only button for toolbar/header chrome (sidebar toggles, expand-all).
 * Same secondary styling as SecondaryButton, sized so it does not dominate the row it sits in.
 * Pass the icon as the child and the label via tooltipProps.
 */
export const CompactIconButton = forwardRef<HTMLButtonElement, PublicButton>((props: PublicButton, ref) => {
    const { classes } = useStyles({});

    return (
        <BaseButton
            ref={ref}
            {...props}
            variant="contained"
            color="secondary"
            className={clsx(classes.compactIcon, props.className)}
        />
    );
});

export const DangerButton = forwardRef<HTMLButtonElement, PublicButton>((props: PublicButton, ref) => {
    return <BaseButton ref={ref} {...props} variant="contained" color="error" />;
});
export const DangerOutlinedButton = forwardRef<HTMLButtonElement, PublicButton>((props: PublicButton, ref) => {
    return <BaseButton ref={ref} {...props} variant="outlined" color="error" />;
});

const TextButton = forwardRef<HTMLButtonElement, PublicButton>((props: PublicButton, ref) => {
    return <BaseButton ref={ref} {...props} variant="text" color="inherit" />;
});

type SplitButtonProps<T> = BoxProps<
    "div",
    {
        menuOptions: {
            label: string;
            value: T;
            disabled?: boolean;
        }[];
        disabled?: boolean;
        loading?: boolean;
        onLeftClick: (value: T) => void;
        tooltipProps?: Omit<TooltipProps, "arrow" | "children">;
    }
>;
export const SplitButton = <T,>(props: SplitButtonProps<T>) => {
    const { menuOptions, disabled, loading, onLeftClick, tooltipProps, ...rest } = props;
    const { t } = useTranslation(["common"]);

    const [value, setValue] = React.useState<T>(props.menuOptions[0].value);
    const anchorRef = React.useRef(null);
    const { classes } = useStyles({});

    return (
        <Box {...rest} style={{ display: "flex", alignItems: "center", gap: "1px", ...rest.style }}>
            <PrimaryButton
                onClick={() => onLeftClick(value)}
                disabled={disabled}
                loading={loading}
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                tooltipProps={tooltipProps}
            >
                {menuOptions.find((option) => option.value === value)?.label}
            </PrimaryButton>

            <DropdownContainer
                icon={<i className="fa-solid fa-caret-down" />}
                anchorRef={anchorRef}
                disabled={disabled || loading}
                tooltipProps={{ title: t("common:tooltip.moreActions") }}
                triggerProps={{ className: classes.splitButton, buttonType: "primary" }}
            >
                {menuOptions.map((option, idx) => (
                    <DropdownItem
                        key={option.label + idx}
                        label={option.label}
                        disabled={option.disabled}
                        onClick={() => setValue(option.value)}
                    />
                ))}
            </DropdownContainer>
        </Box>
    );
};

BaseButton.displayName = "BaseButton";
CompactIconButton.displayName = "CompactIconButton";
PrimaryButton.displayName = "PrimaryButton";
PrimaryOutlinedButton.displayName = "PrimaryOutlinedButton";
SecondaryButton.displayName = "SecondaryButton";
DangerButton.displayName = "DangerButton";
DangerOutlinedButton.displayName = "DangerOutlinedButton";
TextButton.displayName = "TextButton";
SplitButton.displayName = "SplitButton";
